/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Copyright 2026 Bytefinger Technology (HK) Co., Limited. SPDX-License-Identifier: Apache-2.0 */
import { I18n } from '@adp-arch/i18n';

import {
  ResearchTaskQueryProvider,
  useResearchTask,
  type ResearchTaskView as TaskView,
} from './research-task-query';
import {
  type ResearchTaskLabels,
  isTerminalResearchTaskStatus,
  researchTaskCopy,
  stageLabel,
  statusLabel,
  terminalStatusLabel,
} from './research-task-copy';

function reportUpdatedAt(timestamp?: number) {
  if (!timestamp || !Number.isFinite(timestamp)) {
    return null;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return { dateTime: date.toISOString(), label: date.toLocaleString() };
}

export const getResearchTaskIds = (
  units: Array<{
    llmOutput: { content: string; extra_info?: { tool_name?: string } };
    apiResponse?: { content: string };
  }>,
): string[] => [
  ...new Set(
    units.flatMap(unit => {
      if (!unit.apiResponse?.content) {
        return [];
      }
      try {
        if (unit.llmOutput.extra_info?.tool_name !== 'start_deep_research') {
          const call = JSON.parse(unit.llmOutput.content) as {
            name?: unknown;
            function?: { name?: unknown };
          };
          if (
            call.name !== 'start_deep_research' &&
            call.function?.name !== 'start_deep_research'
          ) {
            return [];
          }
        }
        const result = JSON.parse(unit.apiResponse.content) as {
          task_id?: unknown;
        };
        return typeof result.task_id === 'string' &&
          result.task_id.startsWith('research-')
          ? [result.task_id]
          : [];
      } catch {
        return [];
      }
    }),
  ),
];

const ResearchTaskFailure: React.FC<{
  labels: ResearchTaskLabels;
  message?: string;
}> = ({ labels, message }) => (
  <>
    <p className="mt-2" role="status">
      {labels.failed}
    </p>
    {message ? (
      <p className="mt-1 text-sm" role="alert">
        {message}
      </p>
    ) : null}
  </>
);

const ResearchTaskSubmissionUnknown: React.FC<{
  labels: ResearchTaskLabels;
  task: TaskView;
}> = ({ labels, task }) => (
  <>
    <p className="mt-2" role="status" aria-live="polite">
      {statusLabel(task.status, labels, { stage: task.stage })}
    </p>
    <p className="mt-1 text-sm" role="alert">
      {task.error?.code === 'RESEARCH_SUBMISSION_UNKNOWN'
        ? task.error.message || labels.submissionUnknown
        : labels.submissionUnknown}
    </p>
  </>
);

const displayStatus = (status: string) => {
  switch (status) {
    case 'queued':
    case 'waiting_authorization':
    case 'awaiting_plan':
    case 'approved_pending_admission':
    case 'in_progress':
    case 'cancelling':
      return 'In progress';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
    case 'canceled':
      return 'Cancelled';
    case 'interrupted':
      return 'Interrupted';
    default:
      return 'Research';
  }
};

const ResearchTaskDetails: React.FC<{
  deleted: boolean;
  error: string;
  labels: ResearchTaskLabels;
  statusUpdating: boolean;
  task: TaskView;
}> = ({ deleted, error, labels, statusUpdating, task }) => {
  if (deleted) {
    return (
      <p className="mt-2" role="status">
        {labels.deleted}
      </p>
    );
  }
  if (error) {
    return (
      <p className="mt-2" role="alert">
        {error}
      </p>
    );
  }
  if (task.status === 'completed' && task.report) {
    return (
      <>
        <p className="mt-2" role="status">
          {statusLabel(task.status, labels, {
            stage: task.stage,
            coverage: task.report.coverage,
          })}
        </p>
        {task.billing_status === 'usage_unknown' ? (
          <p className="mt-2 text-sm text-amber-700" role="status">
            {labels.billingPending}
          </p>
        ) : null}
        <div className="mt-2 font-semibold">{task.report.title}</div>
        <div className="mt-2 whitespace-pre-wrap break-words">
          {task.report.body}
        </div>
        {task.report.citations?.length ? (
          <div className="mt-3">
            <strong>{labels.sources}</strong>
            <ol className="list-decimal pl-5">
              {task.report.citations
                .filter(citation => /^https?:\/\//i.test(citation.url))
                .map(citation => (
                  <li key={citation.url}>
                    <a
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {citation.title || citation.url}
                    </a>
                  </li>
                ))}
            </ol>
          </div>
        ) : null}
      </>
    );
  }
  if (task.status === 'failed') {
    return (
      <ResearchTaskFailure labels={labels} message={task.error?.message} />
    );
  }
  if (task.stage === 'submission_unknown') {
    return <ResearchTaskSubmissionUnknown labels={labels} task={task} />;
  }
  const outcomeLabel = terminalStatusLabel(task.status, labels);
  if (outcomeLabel) {
    return (
      <p className="mt-2" role="status">
        {outcomeLabel}
      </p>
    );
  }
  const currentStatus = statusLabel(task.status, labels, {
    stage: task.stage,
    coverage: task.report?.coverage,
  });
  const currentStage = stageLabel(task.stage, labels);
  const reportUpdate = reportUpdatedAt(task.partial_updated_at);
  return (
    <>
      <p className="mt-2" role="status" aria-live="polite">
        {currentStatus}
        {currentStage && currentStage !== currentStatus
          ? ` · ${currentStage}`
          : ''}
      </p>
      {task.cancel_status === 'unconfirmed' ||
      task.cancel_status === 'requested' ? (
        <p className="mt-1 text-sm text-amber-700" role="status">
          {labels.cancellationUnconfirmed}
        </p>
      ) : null}
      {task.progress ? (
        <p className="mt-1 text-sm" aria-live="off">
          {task.progress}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-gray-500">
        {labels.reportUpdated}{' '}
        {reportUpdate ? (
          <time dateTime={reportUpdate.dateTime}>{reportUpdate.label}</time>
        ) : (
          labels.reportNotUpdated
        )}
      </p>
      {task.partial_text ? (
        <div className="mt-3 rounded-md bg-gray-50 p-3">
          <p className="mb-2 text-xs font-semibold text-gray-600">
            {labels.liveReport}
          </p>
          <div
            className="max-h-80 overflow-y-auto whitespace-pre-wrap break-words text-sm"
            aria-live="off"
          >
            {task.partial_text}
          </div>
        </div>
      ) : null}
      {statusUpdating ? (
        <p className="mt-2 text-sm" role="status">
          {labels.statusUpdating}
        </p>
      ) : null}
    </>
  );
};

const ResearchTaskActions: React.FC<{
  cancelBusy: boolean;
  deleteBusy: boolean;
  deleted: boolean;
  error: string;
  labels: ResearchTaskLabels;
  onCancel: () => void;
  onDelete: () => void;
  task: TaskView;
}> = props => {
  const {
    cancelBusy,
    deleteBusy,
    deleted,
    error,
    labels,
    onCancel,
    onDelete,
    task,
  } = props;

  return (
    <>
      {!deleted && !error && canCancelResearchTask(task) ? (
        <button
          type="button"
          disabled={cancelBusy}
          onClick={onCancel}
          className="mt-2 underline"
        >
          {labels.cancel}
        </button>
      ) : null}
      {!deleted && !error ? (
        <button
          type="button"
          disabled={deleteBusy}
          onClick={onDelete}
          className="ml-3 mt-2 underline"
        >
          {deleteBusy ? labels.deleting : labels.delete}
        </button>
      ) : null}
    </>
  );
};

function canCancelResearchTask(task: TaskView) {
  return (
    !isTerminalResearchTaskStatus(task.status) &&
    task.status !== 'cancelling' &&
    task.stage !== 'cancelling' &&
    task.stage !== 'submission_unknown' &&
    task.stage !== 'settling' &&
    task.stage !== 'release_pending' &&
    !task.cancel_status
  );
}

const ResearchTaskCardContent: React.FC<{ taskId: string }> = ({ taskId }) => {
  const language = I18n.language || 'en';
  const label =
    language === 'zh-TW'
      ? researchTaskCopy['zh-TW']
      : language.startsWith('zh')
        ? researchTaskCopy['zh-CN']
        : researchTaskCopy.en;
  const {
    task,
    cancelBusy,
    deleteBusy,
    deleted,
    loadError,
    actionError,
    statusUpdating,
    requestCancel,
    requestDelete,
  } = useResearchTask(taskId, label);

  return (
    <section className="mt-3 w-full overflow-hidden rounded-xl border border-solid border-gray-200 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <strong className="text-sm font-semibold">{label.title}</strong>
        {!deleted && !loadError ? (
          <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
            {displayStatus(task.status)}
          </span>
        ) : null}
      </header>
      <div className="space-y-3 p-4">
        <ResearchTaskDetails
          deleted={deleted}
          error={loadError}
          labels={label}
          statusUpdating={statusUpdating}
          task={task}
        />
        {actionError ? (
          <p className="mt-2 text-sm" role="alert">
            {actionError}
          </p>
        ) : null}
        <ResearchTaskActions
          cancelBusy={cancelBusy}
          deleteBusy={deleteBusy}
          deleted={deleted}
          error={loadError || actionError}
          labels={label}
          onCancel={() => void requestCancel()}
          onDelete={() => void requestDelete()}
          task={task}
        />
      </div>
    </section>
  );
};

export const ResearchTaskCard: React.FC<{ taskId: string }> = ({ taskId }) => (
  <ResearchTaskQueryProvider>
    <ResearchTaskCardContent taskId={taskId} />
  </ResearchTaskQueryProvider>
);
